import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import type { DropdownOption } from 'ui-components/components/dropdown/dropdown-types';
import { GuidService } from 'ui-components/services/guid-service';

import { US } from '../../../../../../constants/country-codes';
import { InsuranceProviders } from '../../../../../../enums/insurance-providers';
import { useEnableCarrierInsuranceBanner } from '../../../../../../feature-flags/hooks/use-enable-carrier-insurance-banner';
import { useDisableAutomationRuleInsureCarrier } from '../../../../../../feature-flags/hooks/use-disable-automation-rule-insure-carrier';
import { selectAvailableInsuranceOptions } from '../../../../../../services/insurance-service';
import Global from '../../../../../../store/global-selectors';
import type { WorkflowActionInputProps } from '../../../registries/registry-types';
import { messages } from '../messages';

const getOptionLabel = (option: DropdownOption) => option.label as string;
const getOptionValue = (option: DropdownOption) => String(option.value);

export const InsuranceDropdown: React.FC<WorkflowActionInputProps> = ({ className, draft, onUpdate }) => {
  const intl = useIntl();
  const id = useMemo(() => GuidService.newGuid(), []);
  const availableInsuranceOptions = useSelector((state) => selectAvailableInsuranceOptions({ state }));
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const wallets = useSelector(Global.selectSellerWallets);
  const enableCarrierInsuranceBanner = useEnableCarrierInsuranceBanner();
  const disableAutomationRuleInsureCarrier = useDisableAutomationRuleInsureCarrier();

  const options = useMemo(() => {
    const hasWalletConfigured = wallets && wallets.length > 0;
    const isCarrierRestrictedRegion = homeCountry === US && hasWalletConfigured;

    return availableInsuranceOptions.map(({ insuranceOptionId, messageDescriptor }) => {
      const isCarrierOption = insuranceOptionId === InsuranceProviders.Carrier.insuranceProviderId;
      let finalMessageDescriptor = messageDescriptor;
      if (isCarrierOption && isCarrierRestrictedRegion) {
        if (disableAutomationRuleInsureCarrier) {
          finalMessageDescriptor = messages.insuranceCarrierUnsupported;
        } else if (enableCarrierInsuranceBanner) {
          finalMessageDescriptor = messages.insuranceCarrierEndingSupport;
        }
      }

      return {
        value: insuranceOptionId,
        label: intl.formatMessage(finalMessageDescriptor),
        disabled: isCarrierOption && isCarrierRestrictedRegion && disableAutomationRuleInsureCarrier,
      };
    });
  }, [availableInsuranceOptions, homeCountry, wallets, enableCarrierInsuranceBanner, disableAutomationRuleInsureCarrier, intl]);

  const handleSelect = useCallback(
    (value: string) => {
      onUpdate({ intParameter: value ? Number(value) : null });
    },
    [onUpdate],
  );

  return (
    <SearchableDropdownField
      className={className}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      id={id}
      onItemSelected={handleSelect}
      options={options}
      placeholder={intl.formatMessage(messages.insuranceDropdownPlaceholder)}
      selectedItemValue={draft.intParameter !== null ? String(draft.intParameter) : undefined}
    />
  );
};
